import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CheckBox,
  Layer,
  Heading,
  Text,
  RadioButtonGroup,
} from "grommet";
import { Add, Edit, Trash, Duplicate } from "grommet-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFetchHotel } from "./redux/hooks";
import useAutomationFlows from "./useAutomationFlows";
import AutomationFlowEditor, {
  flowName,
  flowTiming,
} from "./components/AutomationFlowEditor";
import { presentAlertPrompt } from "../shared/Alert";
import "./SettingsAutomationFlows.css";
import OtaEmailNotice from "./components/OtaEmailNotice";
import { PackageUpgradeModal } from "./components/OfferGate";
import { getOfferCapabilities } from "../../common/offerCapabilities";

export function AutomationSlots({
  hotel,
  state,
  busy,
  onConfigure,
  onEdit,
  onAction,
}) {
  const { t } = useTranslation();
  const channels = (flow) => flow.channels || ["email", "sms"];
  const count = (channel) =>
    state.flows.filter(
      (flow) => flow.state === "active" && channels(flow).includes(channel),
    ).length;
  const renderFlow = (flow) => (
    <article className="automation-flow-card" key={flow.id}>
      <header className="automation-flow-header">
        <h2>{flowName(flow, t)}</h2>
        <div className="automation-flow-status">
          <span
            className={`automation-badge${flow.state === "active" ? " automation-badge-on" : flow.state === "disabled" ? " automation-badge-disabled" : ""}`}
          >
            {t(`automation.states.${flow.state}`)}
          </span>
          {flow.state !== "archived" && (
            <CheckBox
              toggle
              checked={flow.state === "active"}
              disabled={busy}
              aria-label={t(
                flow.state === "active"
                  ? "automation.enabled"
                  : "automation.enable",
              )}
              onChange={() =>
                onAction(flow.state === "active" ? "disable" : "enable", flow)
              }
            />
          )}
        </div>
      </header>
      <div className="automation-flow-content">
        <p>
          {flowTiming(flow, t)}
        </p>
        <div className={`automation-channel-badges${flow.state !== "active" ? " automation-channel-badges-muted" : ""}`}>
          {channels(flow).map((channel) => (
            <span key={channel}>{t(`automation.channels.${channel}`)}</span>
          ))}
        </div>
        {flow.kind === "reminder" && (
          <p>{t("automation.reminderDescription")}</p>
        )}
      </div>
      <div className="automation-actions">
        <Button
          size="small"
          hoverIndicator="accent-4"
          primary
          color="status-warning"
          icon={<Edit size="small" />}
          aria-label={t("automation.edit")}
          title={t("automation.edit")}
          disabled={busy}
          onClick={() => onEdit(flow)}
        />
        {flow.kind === "message" && <Button size="small" hoverIndicator={{ background: "brand", color: "white" }} primary color="brand"
          icon={<Duplicate size="small" />} aria-label={t("automation.duplicate")} title={t("automation.duplicate")}
          disabled={busy} onClick={() => onAction("duplicate", flow)} />}
        {flow.kind !== "reminder" && (flow.state === "archived" ? (
          <>
            <Button
              label={t("automation.restore")}
              disabled={busy}
              onClick={() => onAction("restore", flow)}
            />
            <Button
              size="small"
              hoverIndicator="status-error"
              primary
              color="status-critical"
              icon={<Trash size="small" />}
              aria-label={t("automation.delete")}
              title={t("automation.delete")}
              disabled={busy}
              onClick={() => onAction("delete", flow)}
            />
          </>
        ) : (
          <>
            <Button
              size="small"
              hoverIndicator="status-error"
              primary
              color="status-critical"
              icon={<Trash size="small" />}
              aria-label={t("automation.delete")}
              title={t("automation.delete")}
              disabled={busy}
              onClick={() => onAction("delete", flow)}
            />
          </>
        ))}
      </div>
    </article>
  );
  return (
    <>
      <OtaEmailNotice hotel={hotel} />
      {state.notice && (
        <div className="automation-notice" role="status">
          {t(`automation.notices.${state.notice}`)}
        </div>
      )}
      <div className="automation-list-toolbar">
        <p>
          {t("automation.channelSummary", {
            email: count("email"),
            sms: count("sms"),
            smsLimit: hotel?.serviceType === "messaging" ? 3 : 2,
          })}
        </p>
        <Button
          primary
          icon={<Add size="small" />}
          label={t("automation.add")}
          disabled={busy}
          onClick={() => onConfigure()}
        />
      </div>
      <p>{t(hotel?.serviceType === "messaging" ? "automation.channelLimits" : "automation.accessDoorsChannelLimits")}</p>
      <div className="automation-flow-list">
        {state.flows
          .filter((flow) => flow.state !== "archived" && (flow.kind !== "reminder" || !state.flows.some(parent => parent.kind === "precheckin" && parent.state !== "archived")))
          .map(flow => flow.kind === "precheckin" ? (
            <div className="automation-flow-pair" key={flow.id}>
              {renderFlow(flow)}
              {state.flows.filter(child => child.kind === "reminder" && child.state !== "archived").map(child => (
                <div className="automation-reminder-linked" key={child.id}>{renderFlow(child)}</div>
              ))}
            </div>
          ) : renderFlow(flow))}
      </div>
      {!state.flows.some((flow) => flow.state !== "archived") && (
        <p className="automation-empty-body">
          {t("automation.emptyDescription")}
        </p>
      )}
      {state.flows.some((flow) => flow.state === "archived") && (
        <section aria-label={t("automation.archived")}>
          <h2>{t("automation.archived")}</h2>
          <div className="automation-flow-list">
            {state.flows
              .filter((flow) => flow.state === "archived")
              .map(renderFlow)}
          </div>
        </section>
      )}
    </>
  );
}

function Overview({ hotel }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, busy, error, change } = useAutomationFlows(hotel._id);
  const [chooser, setChooser] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [flowKind, setFlowKind] = useState("message");
  const [searchParams, setSearchParams] = useSearchParams();
  const editorId = searchParams.get("flow");
  const editor =
    editorId === "new"
      ? { kind: "message", state: "draft" }
      : state?.flows.find((flow) => flow.id === editorId);
  const setEditor = (flow) =>
    setSearchParams(flow ? { flow: flow.id || "new" } : {});
  const editorReady = Boolean(state);
  useEffect(() => {
    if (!editorId || !editorReady) return;
    const frame = requestAnimationFrame(() =>
      document.getElementById("automation-name")?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [editorId, editorReady]);
  const [confirmation, setConfirmation] = useState(null);
  const [withReminder, setWithReminder] = useState(false);
  const edit = (flow) => {
    if (flow.kind === "message") setEditor(flow);
    else
      navigate(
        `/settings/checkin-flow?flow=${encodeURIComponent(flow.id)}#section-${flow.kind === "reminder" ? "reminder" : "precheckin"}`,
      );
  };
  const createSpecial = async (kind) => {
    const result = await change({ action: "create", kind });
    if (result) {
      setChooser(false);
      edit(result.flows.find((flow) => flow.id === result.flowId));
    }
  };
  const action = async (action, flow) => {
    if (["enable", "restore"].includes(action) && !getOfferCapabilities(hotel).messaging) { setUpgrade(true); return; }
    if (action === "delete") {
      presentAlertPrompt({
        title: t("settingsDoors.deleteConfirmTitle"),
        message: t("automation.confirm.delete"),
        buttonOKText: t("automation.delete"),
        buttonOKColor: "status-critical",
        onOK: () => change({ action: "delete", id: flow.id }),
      });
    } else if (action === "restore" && flow.kind === "precheckin") {
      setWithReminder(false);
      setConfirmation({ action, flow });
    } else await change({ action, id: flow.id });
  };
  const existing = state?.flows.find((flow) => flow.kind === "precheckin");
  if (editorId)
    return (
      <main className="automation-overview automation-editor-page">
        <header className="automation-page-header">
          <h1>
            {t(editorId === "new" ? "automation.configure" : "automation.edit")}
          </h1>
        </header>
        {!state && !error && <p role="status">{t("automation.loading")}</p>}
        {state && !editor && <p role="alert">{t("automation.flowMissing")}</p>}
        {state && editor ? (
          <AutomationFlowEditor
            key={editorId}
            flow={editor}
            flows={state.flows}
            busy={busy}
            onClose={() => setEditor(null)}
            onSave={async (config, revision) => {
              const result = await change(
                editor.id
                  ? { action: "edit", id: editor.id, revision, config }
                  : { action: "create", kind: "message", config },
              );
              if (result) setEditor(null);
              return result;
            }}
          />
        ) : (
          <Button
            label={t("automation.back")}
            onClick={() => setEditor(null)}
          />
        )}
      </main>
    );
  return (
    <main className="automation-overview">
      {upgrade && <PackageUpgradeModal capability="automation" onClose={() => setUpgrade(false)} />}
      <header className="automation-page-header">
        <h1>{t("automation.title")}</h1>
        <p>{t("automation.intro")}</p>
      </header>
      {!state && !error && <p role="status">{t("automation.loading")}</p>}
      {state && (
        <AutomationSlots
          hotel={hotel}
          state={state}
          busy={busy}
          onEdit={edit}
          onAction={action}
          onConfigure={(kind) =>
            kind ? createSpecial(kind) : (existing || hotel.serviceType !== "messaging") ? setEditor({ kind: "message" }) : setChooser(true)
          }
        />
      )}
      {chooser && (
        <Layer
          responsive={false}
          role="dialog"
          aria-modal="true"
          aria-label={t("automation.configure")}
          onEsc={() => setChooser(false)}
          onClickOutside={() => setChooser(false)}
        >
          <Box
            pad="medium"
            gap="medium"
            width="large"
            style={{ maxWidth: "calc(100vw - 32px)" }}
          >
            <Heading level={3} margin="none">
              {t("automation.configure")}
            </Heading>
            <Text color="dark-3">{t("automation.chooseKind")}</Text>
            <RadioButtonGroup
              name="automation-kind"
              value={flowKind}
              onChange={(event) => setFlowKind(event.target.value)}
              options={[
                {
                  value: "message",
                  label: (
                    <Box gap="xsmall">
                      <Text weight="bold">{t("automation.message")}</Text>
                      <Text size="small" color="dark-3">
                        {t("automation.messageChoice")}
                      </Text>
                    </Box>
                  ),
                },
                {
                  value: "precheckin",
                  label: (
                    <Box gap="xsmall">
                      <Text weight="bold">
                        {t(
                          existing
                            ? "automation.openSpecial"
                            : "automation.precheckin",
                        )}
                      </Text>
                      <Text size="small" color="dark-3">
                        {t("automation.precheckinChoice")}
                      </Text>
                    </Box>
                  ),
                },
              ]}
            />
            <Box
              as="footer"
              direction="row"
              justify="end"
              gap="small"
              pad={{ top: "medium", bottom: "small" }}
            >
              <Button
                color="dark-3"
                label={t("automation.close")}
                disabled={busy}
                onClick={() => setChooser(false)}
              />
              <Button
                primary
                label={t("automation.continue")}
                disabled={busy}
                onClick={() => {
                  if (flowKind === "message") {
                    setChooser(false);
                    setEditor({ kind: "message", state: "draft" });
                  } else if (existing) {
                    setChooser(false);
                    existing.state === "archived"
                      ? action("restore", existing)
                      : edit(existing);
                  } else createSpecial("precheckin");
                }}
              />
            </Box>
          </Box>
        </Layer>
      )}
      {confirmation && state && (
        <Layer
          role="dialog"
          aria-modal="true"
          aria-label={t(`automation.${confirmation.action}`)}
          onEsc={() => !busy && setConfirmation(null)}
        >
          <Box pad="medium" gap="medium" width="large">
            <h2>
              {t(`automation.${confirmation.action}`)}:{" "}
              {flowName(confirmation.flow, t)}
            </h2>
            <p>{t(`automation.confirm.${confirmation.action}`)}</p>
            {confirmation.flow.kind === "precheckin" &&
              confirmation.action === "archive" && (
                <p>{t("automation.archiveLinked")}</p>
              )}
            {confirmation.action === "restore" &&
              state.flows.some((flow) => flow.kind === "reminder") && (
                <CheckBox
                  label={t("automation.restoreWithReminder")}
                  checked={withReminder}
                  onChange={(event) => setWithReminder(event.target.checked)}
                />
              )}
            <Button
              primary
              disabled={busy}
              label={t(`automation.${confirmation.action}`)}
              onClick={async () => {
                const result = await change({
                  action: confirmation.action,
                  id: confirmation.flow.id,
                  ...(confirmation.action === "restore"
                    ? { withReminder }
                    : {}),
                });
                if (result) setConfirmation(null);
              }}
            />
            <Button
              disabled={busy}
              label={t("automation.close")}
              onClick={() => setConfirmation(null)}
            />
          </Box>
        </Layer>
      )}
    </main>
  );
}

export default function SettingsAutomationFlows() {
  const { hotel } = useFetchHotel();
  if (!hotel?._id) return null;
  return <Overview key={hotel._id} hotel={hotel} />;
}
